import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { type KeyboardEvent, type PointerEvent, type ReactNode, useCallback, useId, useMemo, useState } from 'react';

export type DataTableValue = boolean | number | string | null | undefined;

export interface DataTableColumn<T> {
  readonly id: string;
  readonly header: string;
  readonly getValue: (item: T) => DataTableValue;
  readonly renderCell: (item: T) => ReactNode;
  readonly sortable?: boolean;
  readonly resizable?: boolean;
  readonly width?: number;
  readonly minWidth?: number;
  readonly maxWidth?: number;
}

interface DataTableProps<T> {
  readonly ariaLabel: string;
  readonly columns: readonly DataTableColumn<T>[];
  readonly getRowId: (item: T) => string;
  readonly items: readonly T[];
  readonly tableId: string;
  readonly limit?: number;
  readonly emptyState?: ReactNode;
  readonly onRowClick?: (item: T) => void;
  readonly rowClassName?: string;
  readonly sortDescription?: string;
}

type SortDirection = 'ascending' | 'descending';

interface SortState {
  readonly columnId: string;
  readonly direction: SortDirection;
}

const MINIMUM_WIDTH = 96;
const DEFAULT_WIDTH = 180;
const MAXIMUM_WIDTH = 640;
const KEYBOARD_RESIZE_STEP = 16;
const STORAGE_VERSION = 'v1';

const useStyles = makeStyles({
  tableWrap: { maxWidth: '100%', overflowX: 'auto' },
  visuallyHidden: { position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 },
  table: { width: '100%', tableLayout: 'fixed' },
  header: { position: 'relative', paddingRight: tokens.spacingHorizontalL },
  headerContent: { display: 'flex', alignItems: 'center', minWidth: 0 },
  sortDirection: { marginLeft: tokens.spacingHorizontalXS },
  sortButton: {
    color: 'inherit',
    font: 'inherit',
    fontWeight: 'inherit',
    backgroundColor: 'transparent',
    border: 0,
    cursor: 'pointer',
    padding: 0,
    textAlign: 'left',
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '2px',
      borderRadius: tokens.borderRadiusSmall,
    },
  },
  resizeHandle: {
    position: 'absolute',
    insetBlock: tokens.spacingVerticalXS,
    insetInlineEnd: 0,
    width: tokens.spacingHorizontalM,
    cursor: 'col-resize',
    outline: 'none',
    ':focus-visible': {
      backgroundColor: tokens.colorBrandBackground2,
      borderRadius: tokens.borderRadiusSmall,
    },
  },
  clickableRow: { cursor: 'pointer' },
  disabledHeader: { cursor: 'default' },
});

function storageKey(tableId: string): string {
  return `hub:data-table-widths:${STORAGE_VERSION}:${tableId}`;
}

function loadWidths(tableId: string): Partial<Record<string, number>> {
  try {
    const stored = window.localStorage.getItem(storageKey(tableId));
    if (stored === null) return {};
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter((entry): entry is [string, number] => typeof entry[1] === 'number'),
    );
  } catch {
    return {};
  }
}

function compareValues(first: DataTableValue, second: DataTableValue): number {
  if (first === null || first === undefined) return second === null || second === undefined ? 0 : 1;
  if (second === null || second === undefined) return -1;
  if (typeof first === 'number' && typeof second === 'number') return first - second;
  if (typeof first === 'boolean' && typeof second === 'boolean') return Number(first) - Number(second);
  return String(first).localeCompare(String(second), undefined, { numeric: true, sensitivity: 'base' });
}

export function DataTable<T>({
  ariaLabel,
  columns,
  getRowId,
  items,
  tableId,
  limit,
  emptyState,
  onRowClick,
  rowClassName,
  sortDescription,
}: DataTableProps<T>) {
  const classes = useStyles();
  const sortDescriptionId = useId();
  const [sort, setSort] = useState<SortState | null>(null);
  const [widths, setWidths] = useState<Partial<Record<string, number>>>(() => loadWidths(tableId));

  const updateWidth = useCallback((column: DataTableColumn<T>, requestedWidth: number) => {
    const minWidth = column.minWidth ?? MINIMUM_WIDTH;
    const maxWidth = column.maxWidth ?? MAXIMUM_WIDTH;
    const width = Math.min(maxWidth, Math.max(minWidth, requestedWidth));
    setWidths((current) => {
      const next = { ...current, [column.id]: width };
      try {
        window.localStorage.setItem(storageKey(tableId), JSON.stringify(next));
      } catch {
        // Column widths remain available for the current session if storage is unavailable.
      }
      return next;
    });
  }, [tableId]);

  const sortedItems = useMemo(() => {
    if (sort === null) return items;
    const column = columns.find((candidate) => candidate.id === sort.columnId);
    if (column === undefined || column.sortable === false) return items;
    return items
      .map((item, index) => ({ item, index }))
      .sort((first, second) => {
        const firstValue = column.getValue(first.item);
        const secondValue = column.getValue(second.item);
        const firstIsNull = firstValue === null || firstValue === undefined;
        const secondIsNull = secondValue === null || secondValue === undefined;
        if (firstIsNull !== secondIsNull) return firstIsNull ? 1 : -1;
        const comparison = compareValues(firstValue, secondValue);
        if (comparison !== 0) return sort.direction === 'ascending' ? comparison : -comparison;
        const rowIdComparison = getRowId(first.item).localeCompare(getRowId(second.item), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
        return rowIdComparison !== 0 ? rowIdComparison : first.index - second.index;
      })
      .map(({ item }) => item);
  }, [columns, getRowId, items, sort]);

  const toggleSort = (column: DataTableColumn<T>) => {
    if (column.sortable === false) return;
    setSort((current) => ({
      columnId: column.id,
      direction: current?.columnId === column.id && current.direction === 'ascending' ? 'descending' : 'ascending',
    }));
  };

  const handleSortKeyDown = (event: KeyboardEvent<HTMLButtonElement>, column: DataTableColumn<T>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleSort(column);
    }
  };

  const startResize = (event: PointerEvent<HTMLInputElement>, column: DataTableColumn<T>) => {
    event.preventDefault();
    const currentWidth = widths[column.id] ?? column.width ?? DEFAULT_WIDTH;
    const startX = event.clientX;
    const onPointerMove = (moveEvent: globalThis.PointerEvent) => {
      updateWidth(column, currentWidth + moveEvent.clientX - startX);
    };
    const stopResize = () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', stopResize);
      document.removeEventListener('pointercancel', stopResize);
    };
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', stopResize, { once: true });
    document.addEventListener('pointercancel', stopResize, { once: true });
  };

  const handleResizeKeyDown = (event: KeyboardEvent<HTMLInputElement>, column: DataTableColumn<T>) => {
    const currentWidth = widths[column.id] ?? column.width ?? DEFAULT_WIDTH;
    const minWidth = column.minWidth ?? MINIMUM_WIDTH;
    const maxWidth = column.maxWidth ?? MAXIMUM_WIDTH;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      updateWidth(column, currentWidth - KEYBOARD_RESIZE_STEP);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      updateWidth(column, currentWidth + KEYBOARD_RESIZE_STEP);
    } else if (event.key === 'Home') {
      event.preventDefault();
      updateWidth(column, minWidth);
    } else if (event.key === 'End') {
      event.preventDefault();
      updateWidth(column, maxWidth);
    }
  };

  if (items.length === 0 && emptyState !== undefined) return emptyState;

  const visibleItems = limit === undefined ? sortedItems : sortedItems.slice(0, limit);

  return (
    <>
      {sortDescription !== undefined ? <span id={sortDescriptionId} className={classes.visuallyHidden}>{sortDescription}</span> : null}
      <div className={classes.tableWrap}>
        <Table aria-label={ariaLabel} aria-describedby={sortDescription !== undefined ? sortDescriptionId : undefined} size="small" className={classes.table}>
          <colgroup>
          {columns.map((column) => (
            <col key={column.id} style={{ width: widths[column.id] ?? column.width ?? DEFAULT_WIDTH }} />
          ))}
          </colgroup>
          <TableHeader>
          <TableRow>
            {columns.map((column) => {
              const sortable = column.sortable !== false;
              const resizable = column.resizable !== false;
              const direction = sort?.columnId === column.id ? sort.direction : 'none';
              const width = widths[column.id] ?? column.width ?? DEFAULT_WIDTH;
              const minWidth = column.minWidth ?? MINIMUM_WIDTH;
              const maxWidth = column.maxWidth ?? MAXIMUM_WIDTH;
              return (
                <TableHeaderCell key={column.id} className={classes.header} aria-label={column.header} aria-sort={sortable ? direction : undefined}>
                  <div className={classes.headerContent}>
                    {sortable ? (
                      <button type="button" className={classes.sortButton} onClick={() => { toggleSort(column); }} onKeyDown={(event) => { handleSortKeyDown(event, column); }} aria-label={`Sort by ${column.header}, currently ${direction}`}>
                        {column.header}
                        {direction === 'ascending' ? <span className={classes.sortDirection} aria-hidden="true">↑</span> : direction === 'descending' ? <span className={classes.sortDirection} aria-hidden="true">↓</span> : null}
                      </button>
                    ) : <span className={classes.disabledHeader}>{column.header}</span>}
                  </div>
                  {resizable ? (
                    <input
                      type="range"
                      aria-label={`Resize ${column.header} column`}
                      aria-orientation="horizontal"
                      min={minWidth}
                      max={maxWidth}
                      value={width}
                      className={classes.resizeHandle}
                      onChange={(event) => { updateWidth(column, Number(event.target.value)); }}
                      onPointerDown={(event) => { startResize(event, column); }}
                      onKeyDown={(event) => { handleResizeKeyDown(event, column); }}
                    />
                  ) : null}
                </TableHeaderCell>
              );
            })}
          </TableRow>
          </TableHeader>
          <TableBody>
          {visibleItems.map((item) => (
            <TableRow
              key={getRowId(item)}
              className={mergeClasses(rowClassName, onRowClick ? classes.clickableRow : undefined)}
              onClick={onRowClick ? () => { onRowClick(item); } : undefined}
            >
              {columns.map((column) => <TableCell key={column.id}>{column.renderCell(item)}</TableCell>)}
            </TableRow>
          ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
